import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const CustomSoftware = () => {
  return (
    <>
      <SEO 
        title="Custom Software Development - Enterprise Solutions"
        description="Build bespoke enterprise applications tailored to your exact business requirements. Expert custom software development with full integration capabilities, ongoing support, and scalable architecture."
        keywords="custom software development, bespoke applications, enterprise software, tailored solutions, software engineering, full-stack development"
      />

      {/* Hero Section */}
      <section className="relative pt-10 pb-10 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-pearl-white via-white to-blush-light/20"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary-red/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-coral-mist/5 rounded-full blur-3xl"></div>
        
        <div className="page-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-slide-up">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-platinum mb-6">
                <span className="flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-primary-red opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-red"></span>
                </span>
                <span className="text-sm font-medium text-charcoal">Enterprise-Grade Solutions</span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 text-charcoal">
                Custom <span className="text-gradient">Software</span><br/>Built for Your Vision
              </h1>
              <p className="text-xl text-titanium mb-8 leading-relaxed">
                Transform your unique business requirements into powerful, scalable software solutions. We build enterprise-grade applications that align perfectly with your workflows and grow with your business.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/contact" className="group relative inline-flex items-center justify-center px-8 py-4 bg-primary-red text-white rounded-xl font-semibold overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-primary-red/25 hover:-translate-y-0.5">
                  <span className="relative z-10">Start Your Project</span>
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <a href="#process" className="inline-flex items-center justify-center px-8 py-4 bg-white text-charcoal rounded-xl font-semibold border-2 border-platinum hover:border-primary-red transition-all duration-300 hover:shadow-lg">
                  See Our Process
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-titanium">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-primary-red mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span>4.9/5 Client Rating</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>ISO 27001 Certified</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>

            <div className="relative animate-fade-in">
              {/* Floating Card with Glassmorphism */}
              <div className="relative">
                {/* Main Card */}
                <div className="relative bg-white/70 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border border-white/50">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6 pb-6 border-b border-platinum/50">
                    <div>
                      <h3 className="text-lg font-semibold text-charcoal mb-1">Development Dashboard</h3>
                      <p className="text-sm text-titanium">Real-time project metrics</p>
                    </div>
                    <div className="flex space-x-1">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    </div>
                  </div>

                  {/* Status Items */}
                  <div className="space-y-4 mb-6">
                    {[
                      { label: '100% Custom Built', value: 'Active', color: 'bg-green-500' },
                      { label: 'Scalable Architecture', value: 'Optimized', color: 'bg-blue-500' },
                      { label: 'Full Integration', value: 'Connected', color: 'bg-purple-500' },
                      { label: 'Ongoing Maintenance', value: 'Monitoring', color: 'bg-primary-red' }
                    ].map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white/50 rounded-xl hover:bg-white/80 transition-all duration-300 group">
                        <div className="flex items-center space-x-3">
                          <div className={`w-2 h-2 ${item.color} rounded-full animate-pulse`}></div>
                          <span className="text-sm font-medium text-charcoal">{item.label}</span>
                        </div>
                        <span className="text-xs px-3 py-1 bg-alabaster rounded-full text-titanium group-hover:bg-primary-red group-hover:text-white transition-colors">
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-primary-red/10 to-coral-mist/10 p-4 rounded-xl border border-primary-red/20">
                      <div className="text-3xl font-bold text-primary-red mb-1">500+</div>
                      <div className="text-xs text-titanium">Projects Delivered</div>
                      <div className="mt-2 flex items-center text-xs text-green-600">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        +23% this year
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-primary-red/10 to-coral-mist/10 p-4 rounded-xl border border-primary-red/20">
                      <div className="text-3xl font-bold text-primary-red mb-1">98%</div>
                      <div className="text-xs text-titanium">Client Retention</div>
                      <div className="mt-2 flex items-center text-xs text-green-600">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        Industry leading
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Accent Cards */}
                <div className="absolute -top-12 -right-8 w-24 h-24 bg-primary-red/10 backdrop-blur-sm rounded-2xl border border-primary-red/20 flex items-center justify-center shadow-lg">
                  <svg className="w-12 h-12 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <div className="absolute -bottom-12 -left-8 w-24 h-24 bg-coral-mist/10 backdrop-blur-sm rounded-2xl border border-coral-mist/20 flex items-center justify-center shadow-lg">
                  <svg className="w-12 h-12 text-coral-mist" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-8 md:py-8 bg-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, #E63946 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>

        <div className="page-container relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block">
              <span className="text-sm font-semibold text-primary-red uppercase tracking-wider mb-4 block">Why Custom Software</span>
              <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6">
                Built for You, Not <span className="text-gradient">Vice Versa</span>
              </h2>
            </div>
            <p className="text-xl text-titanium max-w-3xl mx-auto leading-relaxed">
              Off-the-shelf solutions force you to adapt to their limitations. Custom software adapts to you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
                title: 'Perfect Fit for Your Business',
                description: 'Built specifically for your unique workflows, processes, and requirements—no compromises or workarounds needed.',
                gradient: 'from-primary-red/10 to-coral-mist/10',
                iconBg: 'bg-gradient-to-br from-primary-red to-deep-garnet'
              },
              {
                icon: 'M13 10V3L4 14h7v7l9-11h-7z',
                title: 'Competitive Advantage',
                description: 'Gain unique capabilities that your competitors can\'t replicate with off-the-shelf software, differentiating your business.',
                gradient: 'from-coral-mist/10 to-blush-light/10',
                iconBg: 'bg-gradient-to-br from-coral-mist to-primary-red'
              },
              {
                icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4',
                title: 'Full Data Ownership',
                description: 'Complete control over your data, infrastructure, and intellectual property—no vendor lock-in or data restrictions.',
                gradient: 'from-primary-red/10 to-deep-garnet/10',
                iconBg: 'bg-gradient-to-br from-deep-garnet to-primary-red'
              },
              {
                icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
                title: 'Seamless Integration',
                description: 'Connect perfectly with your existing systems, databases, and third-party tools—no integration headaches.',
                gradient: 'from-coral-mist/10 to-primary-red/10',
                iconBg: 'bg-gradient-to-br from-primary-red to-coral-mist'
              },
              {
                icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
                title: 'Scalability Built-In',
                description: 'Designed to handle your growth trajectory, from startup to enterprise, without performance degradation.',
                gradient: 'from-primary-red/10 to-coral-mist/10',
                iconBg: 'bg-gradient-to-br from-coral-mist to-deep-garnet'
              },
              {
                icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                title: 'Long-Term Cost Efficiency',
                description: 'Lower total cost of ownership over time—no recurring licensing fees, per-user costs, or feature limitations.',
                gradient: 'from-deep-garnet/10 to-primary-red/10',
                iconBg: 'bg-gradient-to-br from-primary-red to-coral-mist'
              }
            ].map((benefit, index) => (
              <div 
                key={index} 
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-platinum hover:border-primary-red/20 hover:-translate-y-2"
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`}></div>
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="mb-6 relative">
                    <div className={`w-16 h-16 ${benefit.iconBg} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={benefit.icon} />
                      </svg>
                    </div>
                    {/* Decorative dot */}
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary-red rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>

                  <h3 className="text-xl font-bold mb-4 text-charcoal group-hover:text-primary-red transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  <p className="text-titanium leading-relaxed">
                    {benefit.description}
                  </p>

                  {/* Arrow indicator */}
                  <div className="mt-6 flex items-center text-primary-red opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-2 transition-all duration-300">
                    <span className="text-sm font-semibold mr-2">Learn more</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section id="process" className="py-5 md:py-5 bg-gradient-to-br from-alabaster via-pearl-white to-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-red/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-coral-mist/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

        <div className="page-container relative z-10">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-primary-red uppercase tracking-wider mb-4 block">How We Work</span>
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6">
              Our Development <span className="text-gradient">Process</span>
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto leading-relaxed">
              A proven methodology that delivers high-quality software on time and within budget
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line - Desktop */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-primary-red via-coral-mist to-primary-red transform -translate-y-1/2"></div>

            <div className="grid lg:grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  title: 'Discovery & Planning',
                  description: 'In-depth analysis of your requirements, workflows, and technical landscape to create a comprehensive project roadmap.',
                  items: ['Requirements gathering', 'Technical feasibility', 'Project timeline', 'Resource allocation'],
                  color: 'from-primary-red to-deep-garnet',
                  position: 'top'
                },
                {
                  step: '02',
                  title: 'Design & Architecture',
                  description: 'Creating intuitive user experiences and robust technical architecture that ensures scalability and maintainability.',
                  items: ['UI/UX design', 'System architecture', 'Database design', 'API specifications'],
                  color: 'from-coral-mist to-primary-red',
                  position: 'bottom'
                },
                {
                  step: '03',
                  title: 'Development & Testing',
                  description: 'Agile development with continuous testing, regular demos, and iterative feedback incorporation.',
                  items: ['Sprint development', 'Code reviews', 'Automated testing', 'Security audits'],
                  color: 'from-primary-red to-coral-mist',
                  position: 'top'
                },
                {
                  step: '04',
                  title: 'Deployment & Support',
                  description: 'Smooth deployment to production with comprehensive training, documentation, and ongoing maintenance.',
                  items: ['Production deployment', 'User training', 'Documentation', '24/7 support'],
                  color: 'from-deep-garnet to-primary-red',
                  position: 'bottom'
                }
              ].map((phase, index) => (
                <div key={index} className="relative group">
                  {/* Connector Circle - Desktop */}
                  <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 z-20" 
                       style={{ top: phase.position === 'top' ? 'calc(100% + 2rem)' : '-2rem' }}>
                    <div className="w-4 h-4 bg-white rounded-full border-4 border-primary-red group-hover:scale-150 transition-transform duration-300 shadow-lg"></div>
                  </div>

                  {/* Card */}
                  <div className={`relative bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-platinum hover:border-primary-red group-hover:-translate-y-2 h-full ${
                    phase.position === 'bottom' ? 'lg:mt-32' : ''
                  }`}>
                    {/* Step Number Badge */}
                    <div className="absolute -top-6 -right-6 z-10">
                      <div className={`w-16 h-16 bg-gradient-to-br ${phase.color} rounded-2xl flex items-center justify-center shadow-2xl transform rotate-12 group-hover:rotate-0 group-hover:scale-110 transition-all duration-500`}>
                        <span className="text-2xl font-bold text-white transform -rotate-12 group-hover:rotate-0 transition-transform duration-500">
                          {phase.step}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <h3 className="text-2xl font-bold mb-4 text-charcoal group-hover:text-primary-red transition-colors duration-300">
                        {phase.title}
                      </h3>
                      <p className="text-titanium mb-6 leading-relaxed">
                        {phase.description}
                      </p>
                      
                      {/* Checklist */}
                      <div className="space-y-3">
                        {phase.items.map((item, idx) => (
                          <div key={idx} className="flex items-start group/item">
                            <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-primary-red/10 to-coral-mist/10 rounded-lg flex items-center justify-center mr-3 group-hover/item:from-primary-red group-hover/item:to-coral-mist transition-all duration-300">
                              <svg className="w-4 h-4 text-primary-red group-hover/item:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <span className="text-sm text-titanium group-hover/item:text-charcoal font-medium transition-colors">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Gradient Overlay on Hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${phase.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl pointer-events-none`}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Below Process */}
          <div className="mt-16 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white/80 backdrop-blur-sm px-8 py-6 rounded-2xl shadow-lg border border-platinum">
              <div className="flex items-center">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-red to-coral-mist border-2 border-white"></div>
                  ))}
                </div>
                <span className="ml-4 text-sm font-semibold text-charcoal">
                  50+ Expert Developers
                </span>
              </div>
              <div className="h-8 w-px bg-platinum hidden sm:block"></div>
              <Link to="/contact" className="text-primary-red font-semibold hover:text-deep-garnet transition-colors flex items-center group">
                Start Your Project Today
                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-5 md:py-5 bg-white relative overflow-hidden">
        {/* Background Grid */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(#E63946 1px, transparent 1px), linear-gradient(90deg, #E63946 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="page-container relative z-10">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-primary-red uppercase tracking-wider mb-4 block">Technology Excellence</span>
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6">
              Our <span className="text-gradient">Technology Stack</span>
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto leading-relaxed">
              We leverage cutting-edge technologies to build robust, scalable, and secure custom applications
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                title: 'Frontend Development',
                icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
                techs: ['React.js & Next.js', 'Vue.js & Nuxt.js', 'Angular', 'TypeScript', 'Tailwind CSS', 'Progressive Web Apps'],
                gradient: 'from-blue-500 to-cyan-500',
                bgPattern: 'from-blue-50 to-cyan-50'
              },
              {
                title: 'Backend Development',
                icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01',
                techs: ['Node.js & Express', 'Python & Django', '.NET Core', 'Java & Spring Boot', 'GraphQL & REST APIs', 'Microservices Architecture'],
                gradient: 'from-primary-red to-coral-mist',
                bgPattern: 'from-red-50 to-pink-50'
              },
              {
                title: 'Infrastructure & DevOps',
                icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z',
                techs: ['AWS, Azure, Google Cloud', 'Docker & Kubernetes', 'CI/CD Pipelines', 'PostgreSQL, MongoDB', 'Redis & Elasticsearch', 'Terraform & Ansible'],
                gradient: 'from-purple-500 to-pink-500',
                bgPattern: 'from-purple-50 to-pink-50'
              }
            ].map((stack, index) => (
              <div key={index} className="group relative">
                {/* Card */}
                <div className="relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-platinum hover:border-transparent h-full overflow-hidden">
                  {/* Gradient Border Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${stack.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-3xl`}></div>
                  
                  {/* Background Pattern */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${stack.bgPattern} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

                  <div className="relative z-10">
                    {/* Icon */}
                    <div className="mb-6">
                      <div className={`w-16 h-16 bg-gradient-to-br ${stack.gradient} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={stack.icon} />
                        </svg>
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold mb-6 text-charcoal group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-primary-red group-hover:to-coral-mist transition-all duration-300">
                      {stack.title}
                    </h3>

                    {/* Technologies List */}
                    <div className="space-y-4">
                      {stack.techs.map((tech, idx) => (
                        <div key={idx} className="flex items-start group/tech">
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-2 h-2 bg-primary-red rounded-full group-hover/tech:scale-150 transition-transform duration-300"></div>
                          </div>
                          <span className="ml-4 text-titanium group-hover/tech:text-charcoal group-hover/tech:translate-x-1 transition-all duration-300 font-medium">
                            {tech}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Tech Badges */}
          <div className="bg-gradient-to-br from-alabaster to-pearl-white rounded-3xl p-8 border border-platinum">
            <h3 className="text-xl font-bold text-charcoal mb-6 text-center">Also Experienced With</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                'Machine Learning', 'AI Integration', 'Blockchain', 'IoT', 
                'Real-time Systems', 'Big Data', 'Serverless', 'Edge Computing',
                'WebSockets', 'Message Queues', 'Caching Strategies', 'API Gateways'
              ].map((tech, index) => (
                <div key={index} className="group px-5 py-2.5 bg-white rounded-xl shadow-sm hover:shadow-lg border border-platinum hover:border-primary-red transition-all duration-300 cursor-default">
                  <span className="text-sm font-medium text-titanium group-hover:text-primary-red transition-colors">
                    {tech}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Types */}
      <section className="pb-5 md:pb-5 bg-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-coral-mist/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-primary-red/5 rounded-full blur-3xl"></div>

        <div className="page-container relative z-10">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-primary-red uppercase tracking-wider mb-4 block">Solutions We Build</span>
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6">
              Types of Custom <span className="text-gradient">Software</span>
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto leading-relaxed">
              From enterprise applications to mobile solutions, we deliver software that drives business value
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Enterprise Resource Planning (ERP)',
                description: 'Comprehensive systems to manage and integrate core business processes across your organization.',
                examples: ['Inventory management', 'Financial systems', 'HR platforms'],
                icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
                gradient: 'from-blue-500 to-cyan-500'
              },
              {
                title: 'Customer Relationship Management (CRM)',
                description: 'Tailored CRM solutions to manage customer interactions, sales pipelines, and marketing campaigns.',
                examples: ['Sales automation', 'Customer portals', 'Analytics dashboards'],
                icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
                gradient: 'from-primary-red to-coral-mist'
              },
              {
                title: 'Business Process Automation',
                description: 'Streamline workflows and eliminate manual tasks with custom automation solutions.',
                examples: ['Workflow engines', 'Document processing', 'Approval systems'],
                icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
                gradient: 'from-purple-500 to-pink-500'
              },
              {
                title: 'Data Analytics Platforms',
                description: 'Custom dashboards and analytics tools to transform raw data into actionable business insights.',
                examples: ['BI dashboards', 'Reporting tools', 'Predictive analytics'],
                icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
                gradient: 'from-green-500 to-teal-500'
              },
              {
                title: 'Mobile Applications',
                description: 'Native and cross-platform mobile apps that extend your business capabilities to any device.',
                examples: ['iOS & Android apps', 'React Native', 'Flutter solutions'],
                icon: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
                gradient: 'from-orange-500 to-red-500'
              },
              {
                title: 'Integration Middleware',
                description: 'Connect disparate systems and create unified data flows across your technology ecosystem.',
                examples: ['API gateways', 'Data synchronization', 'Legacy integration'],
                icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
                gradient: 'from-indigo-500 to-purple-500'
              }
            ].map((type, index) => (
              <div 
                key={index} 
                className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-platinum hover:border-transparent overflow-hidden"
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${type.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="mb-6">
                    <div className={`inline-flex w-16 h-16 bg-gradient-to-br ${type.gradient} rounded-2xl items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={type.icon} />
                      </svg>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold mb-3 text-charcoal group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-primary-red group-hover:to-coral-mist transition-all duration-300">
                    {type.title}
                  </h3>
                  <p className="text-titanium mb-6 leading-relaxed">
                    {type.description}
                  </p>
                  
                  {/* Examples */}
                  <div className="space-y-2">
                    {type.examples.map((example, idx) => (
                      <div key={idx} className="flex items-center text-sm group/item">
                        <div className={`w-1.5 h-1.5 bg-gradient-to-r ${type.gradient} rounded-full mr-3 group-hover/item:scale-150 transition-transform`}></div>
                        <span className="text-primary-red font-medium">{example}</span>
                      </div>
                    ))}
                  </div>

                  {/* Arrow Indicator */}
                  <div className="mt-6 pt-6 border-t border-platinum opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex items-center text-primary-red font-semibold text-sm">
                      <span>Explore solutions</span>
                      <svg className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-8 md:py-8 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-charcoal via-graphite to-charcoal"></div>
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-red rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-coral-mist rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="page-container relative z-10">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-coral-mist uppercase tracking-wider mb-4 block">Track Record</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Proven <span className="text-gradient">Results</span>
            </h2>
            <p className="text-xl text-cool-grey max-w-3xl mx-auto leading-relaxed">
              Delivering measurable results for businesses worldwide
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                metric: '500+', 
                label: 'Custom Applications Delivered', 
                icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                gradient: 'from-primary-red to-coral-mist',
                stat: '+23%',
                statLabel: 'YoY Growth'
              },
              { 
                metric: '98%', 
                label: 'On-Time Delivery Rate', 
                icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
                gradient: 'from-coral-mist to-deep-garnet',
                stat: '100%',
                statLabel: 'Client Satisfaction'
              },
              { 
                metric: '40%', 
                label: 'Average Cost Reduction vs Off-the-Shelf', 
                icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                gradient: 'from-deep-garnet to-primary-red',
                stat: '2-3x',
                statLabel: 'ROI Within Year 1'
              },
              { 
                metric: '3x', 
                label: 'Average Productivity Increase', 
                icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
                gradient: 'from-primary-red to-coral-mist',
                stat: '85%',
                statLabel: 'Process Automation'
              }
            ].map((stat, index) => (
              <div key={index} className="group relative">
                {/* Card */}
                <div className="relative bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10 hover:border-white/20 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl h-full">
                  {/* Gradient Glow Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-3xl blur-xl`}></div>

                  <div className="relative z-10">
                    {/* Icon */}
                    <div className="mb-6">
                      <div className={`w-20 h-20 bg-gradient-to-br ${stat.gradient} rounded-2xl flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={stat.icon} />
                        </svg>
                      </div>
                    </div>

                    {/* Metric */}
                    <div className="mb-4">
                      <div className={`text-5xl md:text-6xl font-bold mb-2 bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                        {stat.metric}
                      </div>
                      <div className="text-white font-medium leading-tight">
                        {stat.label}
                      </div>
                    </div>

                    {/* Additional Stat */}
                    <div className="pt-4 border-t border-white/10">
                      <div className="flex items-center justify-between">
                        <span className="text-cool-grey text-sm">{stat.statLabel}</span>
                        <span className={`text-lg font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                          {stat.stat}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Trust Indicators */}
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {[
              { number: '12+', label: 'Years Experience' },
              { number: '50+', label: 'Expert Developers' },
              { number: '20', label: 'Countries Served' }
            ].map((item, index) => (
              <div key={index} className="text-center group cursor-default">
                <div className="inline-flex flex-col items-center justify-center w-32 h-32 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 group-hover:border-primary-red/50 transition-all duration-300 group-hover:scale-110">
                  <div className="text-3xl font-bold text-white mb-1">{item.number}</div>
                  <div className="text-xs text-cool-grey">{item.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-8 md:py-8 bg-gradient-to-br from-alabaster via-pearl-white to-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #E63946 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="page-container relative z-10">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-primary-red uppercase tracking-wider mb-4 block">Industry Expertise</span>
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6">
              Industries <span className="text-gradient">We Serve</span>
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto leading-relaxed">
              Deep expertise across multiple sectors ensures we understand your unique challenges
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                name: 'Healthcare & Life Sciences', 
                link: '/solutions/healthcare-life-sciences',
                icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
                gradient: 'from-red-500 to-pink-500'
              },
              { 
                name: 'Financial Services', 
                link: '/solutions/financial-services',
                icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                gradient: 'from-green-500 to-emerald-500'
              },
              { 
                name: 'Manufacturing & Logistics', 
                link: '/solutions/manufacturing-logistics',
                icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
                gradient: 'from-blue-500 to-cyan-500'
              },
              { 
                name: 'Retail & E-commerce', 
                link: '/solutions/retail-ecommerce',
                icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
                gradient: 'from-purple-500 to-pink-500'
              },
              { 
                name: 'Technology & Software', 
                link: '/solutions/technology-software',
                icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
                gradient: 'from-indigo-500 to-purple-500'
              },
              { 
                name: 'Government & Public Sector', 
                link: '/solutions/government-public-sector',
                icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
                gradient: 'from-gray-600 to-gray-800'
              },
              { 
                name: 'Education & Training', 
                link: '/solutions/education-training',
                icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
                gradient: 'from-yellow-500 to-orange-500'
              },
              { 
                name: 'Energy & Utilities', 
                link: '/solutions/energy-utilities',
                icon: 'M13 10V3L4 14h7v7l9-11h-7z',
                gradient: 'from-teal-500 to-green-500'
              }
            ].map((industry, index) => (
              <Link 
                key={index} 
                to={industry.link} 
                className="group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-platinum hover:border-transparent overflow-hidden"
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${industry.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="mb-4">
                    <div className={`inline-flex w-14 h-14 bg-gradient-to-br ${industry.gradient} rounded-xl items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 group-hover:bg-white/20 transition-all duration-500`}>
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={industry.icon} />
                      </svg>
                    </div>
                  </div>

                  {/* Industry Name */}
                  <h3 className="font-bold text-charcoal group-hover:text-white transition-colors duration-300 mb-2">
                    {industry.name}
                  </h3>

                  {/* Arrow */}
                  <div className="flex items-center text-primary-red group-hover:text-white transition-colors duration-300">
                    <span className="text-sm font-semibold mr-2">Learn more</span>
                    <svg className="w-4 h-4 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>

                {/* Decorative Corner */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primary-red/5 to-transparent rounded-bl-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </Link>
            ))}
          </div>

          {/* CTA Below Industries */}
          <div className="mt-16 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-6 bg-white rounded-2xl p-8 shadow-xl border border-platinum">
              <div className="flex items-center">
                <div className="flex -space-x-3">
                  {[
                    'from-primary-red to-coral-mist',
                    'from-coral-mist to-deep-garnet',
                    'from-deep-garnet to-primary-red',
                  ].map((gradient, i) => (
                    <div key={i} className={`w-12 h-12 rounded-full bg-gradient-to-br ${gradient} border-2 border-white flex items-center justify-center shadow-lg`}>
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ))}
                </div>
                <div className="ml-6 text-left">
                  <div className="font-bold text-charcoal">Industry-Specific Expertise</div>
                  <div className="text-sm text-titanium">Tailored solutions for your sector</div>
                </div>
              </div>
              <Link to="/solutions" className="px-8 py-4 bg-primary-red text-white rounded-xl font-semibold hover:bg-deep-garnet transition-all duration-300 hover:shadow-lg flex items-center group">
                View All Solutions
                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-5 md:py-5 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-red via-deep-garnet to-primary-red"></div>
        
        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-tr from-coral-mist/20 via-transparent to-primary-red/20 animate-pulse"></div>

        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-coral-mist/10 rounded-full blur-3xl"></div>
        
        {/* Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="page-container relative z-10">
          <div className="max-w-5xl mx-auto">
            {/* Content */}
            <div className="text-center mb-12">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-8 border border-white/20">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-white font-semibold">Transform Your Business Today</span>
              </div>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Ready to Build Your<br/>
                <span className="inline-block mt-2 px-6 py-2 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  Custom Software Solution?
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
                Let's discuss your requirements and create a tailored solution that drives your business forward. Our expert team is ready to transform your vision into reality.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Link 
                  to="/contact" 
                  className="group relative inline-flex items-center justify-center px-10 py-5 bg-white text-primary-red rounded-2xl text-lg font-bold overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                >
                  <span className="relative z-10 flex items-center">
                    Schedule Free Consultation
                    <svg className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </Link>
                
                <Link 
                  to="/solutions" 
                  className="inline-flex items-center justify-center px-10 py-5 bg-white/10 backdrop-blur-sm text-white rounded-2xl text-lg font-bold border-2 border-white/30 hover:bg-white/20 hover:border-white/50 transition-all duration-300"
                >
                  View Industry Solutions
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-white/80 text-sm">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>No commitment required</span>
                </div>
                <div className="hidden sm:block w-px h-4 bg-white/30"></div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Response within 24 hours</span>
                </div>
                <div className="hidden sm:block w-px h-4 bg-white/30"></div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Free project assessment</span>
                </div>
              </div>
            </div>

            {/* Feature Cards Grid */}
            <div className="grid md:grid-cols-3 gap-6 mt-16">
              {[
                {
                  icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
                  title: 'Fast Turnaround',
                  description: 'Get started within 1 week'
                },
                {
                  icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
                  title: 'Dedicated Team',
                  description: 'Expert developers assigned'
                },
                {
                  icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
                  title: 'Quality Assured',
                  description: 'Rigorous testing standards'
                }
              ].map((feature, index) => (
                <div key={index} className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/30 hover:bg-white/10 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-white/20 transition-all duration-300">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={feature.icon} />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-white font-bold mb-1">{feature.title}</h3>
                      <p className="text-white/70 text-sm">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-5 md:py-5 bg-gradient-to-br from-white via-pearl-white to-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-sm font-semibold text-primary-red uppercase tracking-wider mb-4 block">FAQ</span>
              <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6">
                Frequently Asked <span className="text-gradient">Questions</span>
              </h2>
              <p className="text-xl text-titanium">
                Everything you need to know about custom software development
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  question: 'How long does custom software development typically take?',
                  answer: 'Project timelines vary based on complexity, but most custom software projects take 3-9 months from initial planning to deployment. We use agile methodology with regular sprints, allowing you to see progress and provide feedback throughout development.',
                  icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                },
                {
                  question: 'What is the cost of custom software development?',
                  answer: 'Costs depend on project scope, complexity, and required features. While custom development has higher upfront costs than off-the-shelf solutions, it typically provides better ROI through increased efficiency, no recurring licensing fees, and perfect alignment with your needs. We provide detailed cost estimates after requirements analysis.',
                  icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                },
                {
                  question: 'Will I own the source code and intellectual property?',
                  answer: 'Yes, absolutely. You retain full ownership of all source code, documentation, and intellectual property developed for your project. We provide complete code repositories and technical documentation upon project completion.',
                  icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
                },
                {
                  question: 'Do you provide ongoing support and maintenance?',
                  answer: 'Yes, we offer comprehensive maintenance and support packages including bug fixes, security updates, performance optimization, and feature enhancements. We also provide 24/7 emergency support for critical issues.',
                  icon: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.5v7M12 14.5v7M2.5 12h7M14.5 12h7'
                },
                {
                  question: 'Can custom software integrate with our existing systems?',
                  answer: 'Absolutely. Integration with existing systems, databases, and third-party applications is a core part of our development process. We have extensive experience integrating with ERPs, CRMs, legacy systems, and modern APIs.',
                  icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
                },
                {
                  question: 'How do you ensure software quality and security?',
                  answer: 'We implement comprehensive quality assurance including automated testing, code reviews, security audits, and penetration testing. All development follows industry best practices for security, including OWASP guidelines, data encryption, and secure authentication protocols.',
                  icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
                }
              ].map((faq, index) => (
                <div 
                  key={index} 
                  className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-platinum hover:border-primary-red/20"
                >
                  {/* Gradient Background on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-red/5 to-coral-mist/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>

                  <div className="relative z-10">
                    <div className="flex items-start space-x-4">
                      {/* Icon */}
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary-red/10 to-coral-mist/10 rounded-xl flex items-center justify-center group-hover:from-primary-red group-hover:to-coral-mist transition-all duration-300">
                        <svg className="w-6 h-6 text-primary-red group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={faq.icon} />
                        </svg>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-3 text-charcoal group-hover:text-primary-red transition-colors duration-300">
                          {faq.question}
                        </h3>
                        <p className="text-titanium leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact CTA */}
            <div className="mt-12 text-center">
              <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white rounded-2xl p-6 shadow-lg border border-platinum">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-red to-coral-mist rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-charcoal">Still have questions?</div>
                    <div className="text-sm text-titanium">We're here to help!</div>
                  </div>
                </div>
                <Link to="/contact" className="px-6 py-3 bg-primary-red text-white rounded-xl font-semibold hover:bg-deep-garnet transition-all duration-300 hover:shadow-lg">
                  Contact Our Team
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CustomSoftware;